<template>
  <div :class="$style.headerMenuOptionWrapper" aria-hidden="true">
    <div
      :class="[
        $style.inner,
        {
          [$style.innerSymbolAndTextPosition]: isSymbolAndTextMode,
          [$style.innerSymbolPosition]: isSymbolMode,
        },
      ]"
    >
      <div
        v-for="item in items"
        :key="item.tag"
        :class="[
          $style.item,
          {
            [$style.unsetBg]: !item.keepBackground,
            [$style.itemSymbolAndText]: isSymbolAndTextMode,
            [$style.itemSymbol]: isSymbolMode,
          },
        ]"
      >
        <SCASvgIcon v-if="mode !== 'text'" :icon="item.icon" :size="iconSize" />
        <span v-if="mode !== 'symbol'">{{ item.label }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { BuiltinModuleTag } from "@scayla/api-client-fetch/types";
import { SCASvgIcon } from "@scayla/components";
import { computed } from "vue";

import { localizeBuiltinModule } from "@/i18n/helpers";
import { getModuleIcon } from "@/utils/moduleUtils";

interface HeaderMenuOptionItem {
  tag: BuiltinModuleTag;
  keepBackground?: boolean;
}

const LEADING_BOUNDARY_ITEM: HeaderMenuOptionItem = { tag: BuiltinModuleTag.EMAILS };
const CONTACTS_ITEM: HeaderMenuOptionItem = { tag: BuiltinModuleTag.CONTACTS, keepBackground: true };
const DOSSIER_ITEM: HeaderMenuOptionItem = { tag: BuiltinModuleTag.DOCUMENTS };
const TRAILING_BOUNDARY_ITEM: HeaderMenuOptionItem = { tag: BuiltinModuleTag.FILES };

const props = withDefaults(defineProps<{ mode?: "text" | "symbol" | "symbolAndText" }>(), {
  mode: "text",
});

const isSymbolMode = computed(() => props.mode === "symbol");
const isSymbolAndTextMode = computed(() => props.mode === "symbolAndText");
const iconSize = computed(() => (isSymbolMode.value ? "12px" : "10px"));

const items = computed(() => {
  const layout = isSymbolMode.value
    ? [LEADING_BOUNDARY_ITEM, CONTACTS_ITEM, DOSSIER_ITEM, TRAILING_BOUNDARY_ITEM]
    : [CONTACTS_ITEM, DOSSIER_ITEM];

  return layout.map((item) => ({
    ...item,
    icon: getModuleIcon(item.tag) ?? "",
    label: localizeBuiltinModule(item.tag),
  }));
});
</script>

<style lang="scss" module>
.headerMenuOptionWrapper {
  width: 76px;
  height: 32px;
  overflow: hidden;
  position: relative;
  background-color: var(--color-content-brand-primary);
  border-radius: 6px;
  display: flex;
  align-items: center;
}

.inner {
  font-size: 8.143px;
  font-weight: 450;
  line-height: 10.857px;
  letter-spacing: 0.163px;
  display: flex;
  align-items: center;
  gap: 4.5px;
  position: absolute;
  right: -11px;

  &.innerSymbolAndTextPosition {
    right: -3.5px;
  }

  &.innerSymbolPosition {
    right: -8px;
  }
}

.item {
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 4px 8.15px;
  color: var(--color-action-inverted-selected);
  background-color: var(--color-action-primary-selected);
  height: 19px;
  border-radius: 5px;

  &.itemSymbolAndText {
    flex-direction: column;
    gap: 0.71px;
    justify-content: center;
    padding: 1.43px 7.56px;
    font-size: 6.4px;
    border-radius: 5.714px;
    line-height: 6.4px;
    letter-spacing: 0.129px;
    height: 20px;
  }

  &.itemSymbol {
    gap: 4.71px;
    padding: 4.12px;
    height: 20px;
    width: 20px;
  }

  svg path,
  svg {
    fill: currentColor;
  }
}

.unsetBg {
  background-color: unset;
}
</style>
